import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/apiInterceptor';
import { API_BASE_URL } from '../../utils/paths';
import { deleteSpot, updateSpot } from './savedSpotsSlice';
import { isForecastFresh } from '../../utils/forecastCache';

const staleSpotIds = (spotIds, cached) => spotIds.filter((id) => !isForecastFresh(cached[id]));

// spots/all/forecast
export const fetchSavedSpotsDailyForecast = createAsyncThunk(
  'forecast/fetchSavedSpotsDailyForecast',
  async (spotIds, { rejectWithValue, getState }) => {
    try {
      // Only request the spots whose stored forecast is older than the TTL
      const ids = spotIds && spotIds.length ? staleSpotIds(spotIds, getState().dailyForecast.spots) : [];
      const query = ids.length ? `?spot_ids=${ids.join(',')}` : '';
      const response = await apiFetch(`${API_BASE_URL}/spots/all/forecast${query}`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  },
  {
    condition: (spotIds, { getState }) =>
      !spotIds || !spotIds.length || staleSpotIds(spotIds, getState().dailyForecast.spots).length > 0,
  }
);

const daylyForecastSlice = createSlice({
    name: 'dailyForecast',
    initialState: {
        spots: {},
        loadingIds: [],
        isLoading: false,
        errors: null,
        visibleUnitsDaily: {
          overall_rating_score: true,
          wave_height_max: true,
          wave_direction_dominant: false,
          wave_period_max: false,
          wind_speed_10m_max: false,
          wind_direction_10m_dominant: false
        }
    },
    reducers: {
      setVisibleUnitsDaily: (state, action) => {
        state.visibleUnitsDaily = action.payload;
      },
      removeSpotForecast: (state, action) => {
        delete state.spots[action.payload];
      },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSavedSpotsDailyForecast.pending, (state, action) => {
                state.isLoading = true;
                state.errors = null;
                state.loadingIds = action.meta.arg?.length ? staleSpotIds(action.meta.arg, state.spots) : [];
            })
            .addCase(fetchSavedSpotsDailyForecast.fulfilled, (state, action) => {
                state.isLoading = false;
                state.loadingIds = [];
                // Merge (not replace) since a single call may now only cover one page's spots
                state.spots = { ...state.spots, ...action.payload };
            })
            .addCase(fetchSavedSpotsDailyForecast.rejected, (state, action) => {
                state.isLoading = false;
                state.loadingIds = [];
                state.errors = action.payload || { message: 'Failed to fetch all spots forecast' };
            })
            // Listen to spot deletion from savedSpotsSlice
            .addCase(deleteSpot.fulfilled, (state, action) => {
                delete state.spots[action.payload];
            })
            // Preferences/location changed, so the cached ratings are outdated
            .addCase(updateSpot.fulfilled, (state, action) => {
                delete state.spots[action.payload.id];
            });
    }
});

export const { setVisibleUnitsDaily, removeSpotForecast } = daylyForecastSlice.actions;
export default daylyForecastSlice.reducer